// context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { setTokens, clearTokens } from "./AxiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import config from '../Configurations/APIConfig';
import apiService from "../Configurations/APIService";
import DefaultData from "../../CompanyData/DefaultData";

const AuthContext = createContext();

// Returns true if the JWT access token has more than 30 seconds remaining
const isAccessTokenValid = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now() + 30000;
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [landingData, setLandingData] = useState({});

  useEffect(() => {
    let isMounted = true;

    const tryRefresh = async () => {
      const skipPaths = ["/", "/login", "/unauthorized"];
      if (skipPaths.includes(location.pathname)) {
        if (isMounted) setLoading(false);
        return;
      }

      const storedRefreshToken = localStorage.getItem("refreshToken");
      if (!storedRefreshToken) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const storedAccessToken = localStorage.getItem("accessToken");

        // Only call the refresh endpoint when the access token is missing or
        // about to expire. Calling it unnecessarily in every new tab causes
        // token-rotation conflicts and "invalid token" rejections from the API.
        if (!storedAccessToken || !isAccessTokenValid(storedAccessToken)) {
          const res = await apiService.post(config.REFRESH, { refreshToken: storedRefreshToken });
          const newAccessToken = res.accessToken;
          const newRefreshToken = res.refreshToken || storedRefreshToken;
          setTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });
        }

        const profile = await apiService.getSingle(config.GETUSERPROFILE);
        if (isMounted) {
          setUser({
            id: profile.userId,
            cid: profile.companyId,
            bid: profile.branchId,
            role: profile.roleName,
            name: profile.name,
            email: profile.email,
            phoneNumber: profile.phoneNumber,
            currency: profile.currency,
            logo: profile.logo,
            brandName: profile.brandName,
            companyname: profile.companyName,
            companygstno: profile.companyGSTNo,
            companypancard: profile.companyPanCard,
            companyaddress: profile.companyAddress,
            planType: profile.planType,
            endDate: profile.endDate,
            isSupplierAPI: profile.isSupplierAPI,
            parentCompanyId: profile.parentCompanyId,
            walletBalance: profile.walletBalance,
          });
        }
        await GetLandingPage();
      } catch (err) {
        console.error("Token refresh failed:", err);
        if (isMounted) await logout();
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    tryRefresh();
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async ({ accessToken, refreshToken }) => {
    if (!accessToken || !refreshToken) {
      console.error("Missing accessToken or refreshToken");
      return;
    }
    // 🔐 Store both tokens
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    //setTokens(accessToken);
    setTokens({
      accessToken: accessToken,
      refreshToken: refreshToken,
    });

    const profile = await apiService.getSingle(config.GETUSERPROFILE);
    setUser({
      id: profile.userId,
      cid: profile.companyId,
      bid: profile.branchId,
      role: profile.roleName,
      name: profile.name,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      currency: profile.currency,
      logo: profile.logo,
      brandName: profile.brandName,
      companyname: profile.companyName,
      companygstno: profile.companyGSTNo,
      companypancard: profile.companyPanCard,
      companyaddress: profile.companyAddress,
      planType: profile.planType,
      endDate: profile.endDate,
      isSupplierAPI: profile.isSupplierAPI,
      parentCompanyId: profile.parentCompanyId,
      walletBalance:profile.walletBalance
    });
    await GetLandingPage();
    navigate("/flight/search");
  };
  const GetLandingPage = async () => {
    const landingResponse = await apiService.getAll(`${config.GETLANDINGBYCOMPANY}/${DefaultData?.DEFAULTCOMPANYID}`);
    if (landingResponse.data) {
      setLandingData(landingResponse.data[0]);
    }
  }

  const logout = async () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearTokens(); // Assuming this clears any axios headers
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, landingData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
